import type { UnifiedTask } from "src/services";
import type { DocStatus } from "src/types";
import * as yup from "yup";
import { array, boolean, mixed, number, object, string } from "yup";

export const DASHBOARD_CARD_KEYS = [
  "overall-progress",
  "documents-currently-routing",
  "outdated-due-soon",
  "open-tasks",
  "activity-last-7-days",
  "unresolved-comments",
] as const;

export type DashboardCardKey = (typeof DASHBOARD_CARD_KEYS)[number];

export const DASHBOARD_FILTER_KEYS = [
  "document-author",
  "document-approver",
  "task-due-date",
  "task-status",
  "tasks-assignee",
  "document-status",
  "document-comment-last-reply-author",
  "document-comment-last-reply-date",
] as const;

export type DashboardFilterKey = (typeof DASHBOARD_FILTER_KEYS)[number];

export const DASHBOARD_PROGRESS_SEGMENT_KEYS = [
  "inDraft",
  "routing",
  "live",
  "remaining",
] as const;

export type DashboardProgressSegmentKey =
  (typeof DASHBOARD_PROGRESS_SEGMENT_KEYS)[number];

export type DashboardDatePreset =
  | "overdue"
  | "today"
  | "next_7_days"
  | "no_due_date";

export type DashboardDateFilterValue = {
  preset?: DashboardDatePreset;
  from?: string;
  to?: string;
};

export type DashboardUserFilterValue = {
  userIds: string[];
};

export type DashboardAssigneeFilterValue = DashboardUserFilterValue & {
  includeUnassigned?: boolean;
};

export type DashboardFilterValue =
  | DashboardDateFilterValue
  | DashboardUserFilterValue
  | DashboardAssigneeFilterValue
  | string[];

export type DashboardCardFilters = Partial<
  Record<DashboardFilterKey, DashboardFilterValue>
>;

export type DashboardPreferencesByCard = Partial<
  Record<DashboardCardKey, DashboardPreference>
>;

export type PatchDashboardCardPreferencesBody = {
  deviceId?: string | null;
  filters: Partial<Record<DashboardFilterKey, DashboardFilterValue | null>>;
};

export const DashboardProgressSegmentSchema = object({
  key: mixed<DashboardProgressSegmentKey>()
    .oneOf(DASHBOARD_PROGRESS_SEGMENT_KEYS)
    .required(),
  label: string().required(),
  count: number().required(),
});

export const DashboardOverallProgressSchema = object({
  total: number().required(),
  segments: array().of(DashboardProgressSegmentSchema).required(),
});

export const DashboardUserWorkloadSchema = object({
  assignedTaskCount: number().required(),
  assignedOverdueTaskCount: number().required(),
  assignedDueSoonTaskCount: number().required(),
  assignedOtherOpenTaskCount: number().required(),
  unresolvedCommentCount: number().required(),
  total: number().required(),
});

export type DashboardTaskItem = UnifiedTask & {
  type: "task";
};

export type DashboardApprovalDocumentItem = {
  type: "approval-document";
  documentId: string;
  title: string;
  templateKey: string | null;
  deviceId: string | null;
  status: DocStatus;
  latestVersionId: string;
  approvers: Array<{
    id: string;
    userId: string | null;
    approvalStatus: string | null;
    updatedAt: string;
    user: {
      id: string;
      email: string;
      firstName: string | null;
      lastName: string | null;
    };
  }>;
};

export type DashboardUnresolvedCommentDocument = {
  type: "unresolved-comment-document";
  documentId: string;
  documentTitle: string;
  deviceId: string | null;
  status: DocStatus;
  latestVersionId: string;
  unresolvedThreadIds: string[];
};

export type DashboardResolvedCommentDocument = {
  type: "resolved-comment-document";
  documentId: string;
  documentTitle: string;
  deviceId: string | null;
  status: DocStatus;
  latestVersionId: string;
  resolvedThreadIds: string[];
  resolvedThreadCount: number;
  lastResolvedAt: string;
};

export type DashboardCardItem =
  | DashboardTaskItem
  | DashboardApprovalDocumentItem
  | DashboardUnresolvedCommentDocument
  | DashboardResolvedCommentDocument;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const DashboardCardItemSchema = mixed<DashboardCardItem>()
  .required()
  .test(
    "resolved-comment-document-resolved-thread-ids",
    "Resolved comment documents require resolvedThreadIds",
    (value) =>
      !isRecord(value) ||
      value.type !== "resolved-comment-document" ||
      Array.isArray(value.resolvedThreadIds)
  );

export const DashboardActivityDetailsSchema = object({
  completedTasksLastWeek: array().of(DashboardCardItemSchema).required(),
  fullyApprovedDocumentsLastWeek: array()
    .of(DashboardCardItemSchema)
    .required(),
  resolvedCommentDocumentsLastWeek: array()
    .of(DashboardCardItemSchema)
    .required(),
});

export const DashboardPreferenceSchema = object({
  cardKey: mixed<DashboardCardKey>().oneOf(DASHBOARD_CARD_KEYS).required(),
  filters: object().default({}),
});

export const DashboardCardSchema = object({
  key: mixed<DashboardCardKey>().oneOf(DASHBOARD_CARD_KEYS).required(),
  title: string().required(),
  filterKeys: array()
    .of(mixed<DashboardFilterKey>().oneOf(DASHBOARD_FILTER_KEYS).required())
    .required(),
  filters: object().default({}),
  placeholder: boolean().optional(),
  count: number().optional(),
  progress: DashboardOverallProgressSchema.default(undefined).optional(),
  userWorkload: DashboardUserWorkloadSchema.default(undefined).optional(),
  activity: DashboardActivityDetailsSchema.default(undefined).optional(),
  items: array().of(DashboardCardItemSchema).optional(),
});

export const DashboardResponseSchema = object({
  organizationId: string().uuid().required(),
  deviceId: string().uuid().nullable().defined(),
  cards: array().of(DashboardCardSchema).required(),
  preferences: object().default({}),
});

export const DashboardPreferencesResponseSchema = object({
  organizationId: string().uuid().required(),
  deviceId: string().uuid().nullable().defined(),
  preferences: object().default({}),
});

export interface DashboardProgressSegment
  extends yup.InferType<typeof DashboardProgressSegmentSchema> {}

export interface DashboardOverallProgress
  extends Omit<
    yup.InferType<typeof DashboardOverallProgressSchema>,
    "segments"
  > {
  segments: DashboardProgressSegment[];
}

export interface DashboardUserWorkload
  extends yup.InferType<typeof DashboardUserWorkloadSchema> {}

export interface DashboardActivityDetails
  extends Omit<
    yup.InferType<typeof DashboardActivityDetailsSchema>,
    | "completedTasksLastWeek"
    | "fullyApprovedDocumentsLastWeek"
    | "resolvedCommentDocumentsLastWeek"
  > {
  completedTasksLastWeek: DashboardTaskItem[];
  fullyApprovedDocumentsLastWeek: DashboardApprovalDocumentItem[];
  resolvedCommentDocumentsLastWeek: DashboardResolvedCommentDocument[];
}

export interface DashboardPreference
  extends yup.InferType<typeof DashboardPreferenceSchema> {
  filters: DashboardCardFilters;
}

export interface DashboardCard
  extends Omit<
    yup.InferType<typeof DashboardCardSchema>,
    "items" | "activity" | "progress" | "userWorkload"
  > {
  filters: DashboardCardFilters;
  progress?: DashboardOverallProgress;
  userWorkload?: DashboardUserWorkload;
  activity?: DashboardActivityDetails;
  items?: DashboardCardItem[];
}

export interface DashboardResponse
  extends Omit<
    yup.InferType<typeof DashboardResponseSchema>,
    "cards" | "preferences"
  > {
  cards: DashboardCard[];
  preferences: DashboardPreferencesByCard;
}

export interface DashboardPreferencesResponse
  extends Omit<
    yup.InferType<typeof DashboardPreferencesResponseSchema>,
    "preferences"
  > {
  preferences: DashboardPreferencesByCard;
}
